import React, { useEffect, useMemo, useState } from 'react'
import { CheckCircle2, Loader2, Circle, ChevronDown, ChevronUp, XCircle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { cn } from '../lib/utils'

export type StageStatus = 'pending' | 'running' | 'completed' | 'failed'

export interface StageProgress {
  id: string
  name: string
  description: string
  status: StageStatus
}

interface StageProgressListProps {
  stages: StageProgress[]
  className?: string
  activeAgentName?: string
  activeStage?: string
  activeMessage?: string
  activeStatus?: string
  startedAt?: number
  variant?: 'sidebar' | 'bar'
}

export function StageProgressList({
  stages,
  className,
  activeAgentName,
  activeStage,
  activeMessage,
  activeStatus,
  startedAt,
  variant = 'sidebar',
}: StageProgressListProps) {
  const formattedStage = activeStage
    ? activeStage.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    : null

  const [isExpanded, setIsExpanded] = useState(false)

  useEffect(() => {
    if (activeStatus === 'failed') {
      setIsExpanded(true)
    }
  }, [activeStatus])

  const [nowMs, setNowMs] = useState<number>(() => Date.now())
  useEffect(() => {
    if (!startedAt) return
    if (activeStatus === 'completed') return

    const id = window.setInterval(() => setNowMs(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [startedAt, activeStatus])

  const elapsedSeconds = useMemo(() => {
    if (!startedAt) return null
    const diff = Math.max(0, Math.floor((nowMs - startedAt) / 1000))
    return diff
  }, [nowMs, startedAt])

  const formattedElapsed = useMemo(() => {
    if (elapsedSeconds == null) return null
    const hours = Math.floor(elapsedSeconds / 3600)
    const minutes = Math.floor((elapsedSeconds % 3600) / 60)
    const seconds = elapsedSeconds % 60
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    }
    return `${minutes}:${String(seconds).padStart(2, '0')}`
  }, [elapsedSeconds])

  const stageMeta = useMemo(() => {
    const total = stages.length
    const completed = stages.filter((s) => s.status === 'completed').length
    const hasActive = stages.some((s) => s.status === 'running' || s.status === 'failed')
    const current = Math.min(total, completed + (hasActive ? 1 : completed < total ? 1 : 0))
    return { current, total }
  }, [stages])

  if (variant === 'bar') {
    return (
      <Card className={cn(className)}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                {activeStatus === 'failed' ? (
                  <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                ) : activeStatus && activeStatus !== 'completed' ? (
                  <Loader2 className="w-4 h-4 text-primary animate-spin flex-shrink-0" />
                ) : activeStatus === 'completed' ? (
                  <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                ) : null}
                <p className="text-sm font-medium text-foreground truncate">
                  {activeAgentName || 'Workflow'}
                </p>
                {formattedStage && (
                  <p className="text-xs text-muted-foreground truncate">
                    {formattedStage}{activeStatus ? ` • ${activeStatus}` : ''}
                  </p>
                )}
              </div>
              {activeMessage && (
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed truncate">
                  {activeMessage}
                </p>
              )}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="text-xs px-3 py-1.5 rounded-md border text-muted-foreground whitespace-nowrap">
                Stage {stageMeta.current}/{stageMeta.total}
                {formattedElapsed ? ` • ${formattedElapsed}` : ''}
              </div>
              <button
                type="button"
                onClick={() => setIsExpanded((v) => !v)}
                className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border"
              >
                {isExpanded ? 'Hide details' : 'Details'}
                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {isExpanded && (
            <div className="mt-4 overflow-x-auto">
              <div className="flex items-start gap-4 min-w-max">
                {stages.map((stage) => {
                  const isCompleted = stage.status === 'completed'
                  const isRunning = stage.status === 'running'
                  const isFailed = stage.status === 'failed'

                  return (
                    <div key={stage.id} className="flex items-center gap-2 pr-4 border-r last:border-r-0">
                      {isCompleted ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      ) : isFailed ? (
                        <XCircle className="w-4 h-4 text-red-500" />
                      ) : isRunning ? (
                        <Loader2 className="w-4 h-4 text-primary animate-spin" />
                      ) : (
                        <Circle className="w-4 h-4 text-muted-foreground" />
                      )}
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-foreground whitespace-nowrap">{stage.name}</p>
                        <p className="text-[11px] text-muted-foreground whitespace-nowrap">{stage.description}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={cn('h-full', className)}>
      <CardHeader className="pb-4">
        <CardTitle className="text-base">Workflow Progress</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {(activeAgentName || formattedStage || activeMessage) && (
          <div className="rounded-md border border-border bg-background/50 p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                {activeAgentName && (
                  <p className="text-sm font-medium text-foreground truncate">
                    {activeAgentName}
                  </p>
                )}
                {formattedStage && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {formattedStage}{activeStatus ? ` • ${activeStatus}` : ''}
                  </p>
                )}
                {activeMessage && (
                  <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                    {activeMessage}
                  </p>
                )}
              </div>

              {activeStatus && activeStatus !== 'completed' && (
                <Loader2 className="w-4 h-4 text-primary animate-spin flex-shrink-0 mt-0.5" />
              )}
            </div>
          </div>
        )}

        {stages.map((stage) => {
          const isCompleted = stage.status === 'completed'
          const isRunning = stage.status === 'running'

          return (
            <div key={stage.id} className="flex items-start gap-3">
              <div className="mt-1">
                {isCompleted ? (
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                ) : isRunning ? (
                  <Loader2 className="w-5 h-5 text-primary animate-spin" />
                ) : (
                  <Circle className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">
                  {stage.name}
                </p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {stage.description}
                </p>
              </div>
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}
